import { Component, Input, ViewChild, ElementRef, Output, EventEmitter, ViewEncapsulation, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionHandlerDirective } from '../../directives/subscription-handler.directive';
import { getCaretPosition, getCharAt, removeCharAt } from '../chat-mention';
import { ChatConfigRequest, ChatConfigResponse, ChatOption, MentionItem } from '../../models/chat.models';
import { ChatService } from '../../services/chat.service';
import { AppPermissionService } from '../../services/app-permission.service';
import { ReservedTags, SkillType } from '../../enums/chat.enum';
import { UploadOption, UploadType } from '../../enums/upload.enum';
import { filter, take } from 'rxjs/operators';
import { IndexedDbService } from '../../services/indexed-db.service';
import { CHAT_INDEXED_DB_CONFIG } from '../../enums/event.enum';
export const KEY_ENTER = 'Enter';
export const KEY_ESCAPE = 'Escape';
export const EQUIP = "equip";
export const CCU = "ccu";
export const ZONE = "zone";
export const FILTER_ENTITY_TYPES = [CCU, EQUIP];

@Component({
    selector: 'app-chat-input',
    templateUrl: './chat-input.component.html',
    styleUrls: ['./chat-input.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatInputComponent extends SubscriptionHandlerDirective {
    @Input() isMobileApp: boolean = false;
    @Input() isCCU: boolean = false;
    @Input() isAndroid: boolean = false;
    @Input() config: ChatConfigRequest;
    @Input() buildingEntityList: any[] = [];
    @Input() disableInput: boolean = false;
    @Input() chatScreenConfig:ChatConfigResponse;
    @Input() sendIconEnabled: boolean = false;
    @Input() uploadOptionsEnabled: boolean = true;
    @Output() sendMessgae = new EventEmitter();
    @Input() isEmbedded: boolean = false;
    @Input() isInsideChartCommandPanel!: boolean;
    @Input() isDarkTheme: boolean = false;

    _placeholder: string;
    @Input() set placeholder(value: string) {
        this._placeholder = value;
    }

    get placeholder(): string {
        return this._placeholder;
    }

    get showPlaceholder(): boolean {
       return !this.inputText?.length;
    }

    // File Uploads
    selectedOption: UploadOption;
    imageUrl: any;
    inputOptions: ChatOption[] = [];
    fileUploadPlaceholder: string;
    fileUploadType: UploadType | null;
    quickOptionPrompt: string = '';
    showUploadOptions: boolean = false;
    showCameraDialog: boolean = false;

    @Input() showUserInputOptions: boolean;

    @Input() loading: boolean;

    @ViewChild('inputElement', { read: ElementRef }) inputElement: ElementRef<HTMLInputElement>;
    @ViewChild('inputElementText', { static: false }) inputElementText: ElementRef<HTMLTextAreaElement>;

    @Output() toggleMic: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() submit: EventEmitter<string> = new EventEmitter<string>();
    @Output() mentions: EventEmitter<MentionItem[]> = new EventEmitter<MentionItem[]>();
    @Output() cameraDialogOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();


    constructor(
        private readonly chatService: ChatService,
        private readonly appPermissionService: AppPermissionService,
        private indexedDbService : IndexedDbService
    ) {
        super();
    }

    private inputText: string = '';
    setInputText(text: string) {
        this.inputText = text;
        if (!this.isEmbedded && this.inputElement?.nativeElement) {
            this.inputElement.nativeElement.textContent = text;
            this.placeCaretAtEnd(this.inputElement.nativeElement);
        }
        this.focus();
        // Emit input event only when using the content-editable input (not embedded)
        if (!this.isEmbedded && this.inputElement?.nativeElement) {
            const inputEvent = new Event('input', { bubbles: false });
            this.inputElement.nativeElement.dispatchEvent(inputEvent);
        }
    }


    getInputText(): string {
        return this.inputText;
    }

    showMentionsMenu: boolean = false;
    openMentionsMenu() {
        this.focus();
        this.showMentionsMenu = true;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['buildingEntityList'] && !this.isEmbedded) {
            this.buildingEntityList = changes['buildingEntityList'].currentValue;
            const items: MentionItem[] = this.buildingEntityList
                .filter(e => e.type == CCU || (e.type == EQUIP && e.markers.includes(ZONE)))
                .map((entity: any) => {
                    return {
                        id: entity?._id ?? entity?.id,
                        name: entity.name,
                        type: entity.type
                    };
                });
            this.mentionItems = items;
        }

        const disableBecameEnabled =
            changes['disableInput'] &&
            !changes['disableInput'].firstChange &&
            changes['disableInput'].previousValue === true &&
            changes['disableInput'].currentValue === false;

        const loadingFinished =
            changes['loading'] &&
            !changes['loading'].firstChange &&
            changes['loading'].previousValue === true &&
            changes['loading'].currentValue === false &&
            !this.disableInput;

        if ((disableBecameEnabled || loadingFinished) && !this.isMobileApp && !this.isCCU) {
            // Let Angular finish rendering textarea/contenteditable before trying to focus the input
            setTimeout(() => this.setInputCursor(), 0);
        }


        if ((changes['disableInput'] && changes['disableInput'].currentValue || (changes['loading']) && changes['loading'].currentValue)) {
            this.showUploadOptions = false;
            this.showMentionsMenu = false;
        }
    }

    // Speech recognition
    micEnabled: boolean = false;

    // Mentions
    mentionItems: any[] = [];
    mentionConfig: any = {
        triggerChar: '@',
        labelKey: 'name',
        groupKey: 'type',
        groupLabels: { [CCU]: 'CCUs', [EQUIP]: 'Zones' },
        allowSpace: true,
        dropUp: true,
        mentionSelect: (_: any) => { return '' }, /** Do nothing here. Let event emitter drive changes to the contenteditable */
    }

    /**
     * Insert mention at location specified by the event.
     * If mention already exists and is updated, modify the existing mention.
     * If mention does not exist, insert the mention at the caret position.
     * @param event 
     */
    addMention(event: any) {
        const caretPos = getCaretPosition(this.inputElement.nativeElement, null, 'mention');

        if (getCharAt(caretPos - 1, this.inputElement.nativeElement) !== this.mentionConfig.triggerChar) {
            return;
        }

        removeCharAt(caretPos - 1, this.inputElement.nativeElement);

        // Build the mention chip
        const chip = document.createElement('span');
        chip.contentEditable = 'false';
        chip.className = 'mention-item';
        chip.textContent = event.item.name;

        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;

        const range = sel.getRangeAt(0);
        range.collapse(true);
        range.insertNode(chip);

        // Move caret to just after the chip
        range.setStartAfter(chip);
        range.setEndAfter(chip);
        sel.removeAllRanges();
        sel.addRange(range);

        // Fire input so Angular bindings update
        this.inputElement.nativeElement.dispatchEvent(
            new Event('input', { bubbles: true })
        );

        this.focus();
        this.placeCaretAtEnd(this.inputElement.nativeElement);

        this.mentions.emit([event.item]);
    }

    // File upload
    fileReader: FileReader = new FileReader();
    selectedFile: File | null = null;
    selectedPhoto: Blob | null = null;
    @ViewChild('fileUpload') private fileUpload?: ElementRef;

    clearInput() {

        // Two Inputs are for different purposes one for the Safrron AI Chat Bot another is on AI Generative Dashboards
        if (this.inputElement?.nativeElement) {
          this.inputElement.nativeElement.innerHTML = '';
        }
      
        if (this.inputElementText?.nativeElement) {
          const textarea = this.inputElementText.nativeElement;
          textarea.innerHTML = '';
          textarea.value = '';
          textarea.style.height = '36px'; // Reset to min height
        }
      
        this.imageUrl = null;
        this.inputText = '';
        this.quickOptionPrompt = '';
      }

    resetInput() {
        if (this.inputElement?.nativeElement) {
          this.inputElement.nativeElement.blur();
          this.inputElement.nativeElement.innerHTML = '';
        }
      
        if (this.inputElementText?.nativeElement) {
          const textarea = this.inputElementText.nativeElement;
          textarea.blur();
          textarea.innerHTML = '';
          textarea.value = '';
          textarea.style.height = '36px'; // Reset to min height
        }
      
        this.imageUrl = null;
        this.inputText = '';
        this.quickOptionPrompt = '';
      }
      

    setInputCursor() {
        // In embedded mode, wait until textarea is available
        const elementReady = this.isEmbedded
            ? !!this.inputElementText?.nativeElement
            : !!this.inputElement?.nativeElement;

        if (elementReady) {
            this.focus();
        } else {
            setTimeout(() => this.setInputCursor(), 0);
        }
    }

    /**
     * Invoked when a file is selected by the user.
     * If the file is an image, display the image. If skill options are enabled, also show the vision skills.
     * @param event 
     */
    onFileSelected(event: any) {
        this.showUploadOptions = false;
        let files: FileList = event.target.files;
        if (files.length > 0) {
            this.selectedFile = files[0];
            if (this.selectedFile) {
                this.fileUploadType = this.selectedFile.type.startsWith('image') ? UploadType.IMAGE : (this.selectedFile.type === 'application/pdf' ? UploadType.PDF : UploadType.FILE);
                if (this.fileUploadType == UploadType.IMAGE) {
                    this.setImageDataUrl(this.selectedFile);
                    // Updating the quick option prompt, only if the image is uploaded (mobile app)
                    if (this.isMobileApp && this.quickOptionPrompt) {
                        this.setInputText(this.quickOptionPrompt);
                    }
                }

                // Storing the uploaded file(Img/PDF) in Index db storage for further use in the navigation flow for (whiteboard to dashboard)
                const isSupportedFile = this.fileUploadType === UploadType.IMAGE || this.fileUploadType === UploadType.PDF;
                if (!this.isMobileApp && isSupportedFile) {
                    this.indexedDbService.saveFile(this.selectedFile, CHAT_INDEXED_DB_CONFIG.FIELD_NAME, CHAT_INDEXED_DB_CONFIG.DB_NAME, CHAT_INDEXED_DB_CONFIG.STORE_NAME);
                }
                // Only show user skill options for IMAGE or PDF
                if (this.showUserInputOptions && isSupportedFile) {
                    this.displayUserInputOptions(this.fileUploadType);
                }
            }
            if (!this.isMobileApp && !this.isCCU) {
                this.focus();
            }
        }
    }

    getDisplayContent(option: ChatOption): string {
        return this.chatService.getDisplayContent(option);
    }

    onClickUpload(from?: string) {
        this.showUploadOptions = !this.showUploadOptions;
        if (from == 'upload') {
            this.showUserInputOptions = true; // Show user input options when upload options are clicked manually
        }
        if (this.isMobileApp) {
            this.appPermissionService.receiveAppCameraPermissionStatus()
                .pipe(filter(status => status === true), take(1))
                .subscribe(() => {
                    this.onUploadFile();
                });
            this.appPermissionService.requestAppCameraPermissions().next(true);
        }
    }

    onOpenCameraDialog() {
        if (this.isMobileApp || this.isCCU) {
            this.appPermissionService.receiveAppCameraPermissionStatus().
                pipe(filter(status => status === true), take(1))
                .subscribe(() => {
                    this.onUploadFile('camera');
                });
            this.appPermissionService.requestAppCameraPermissions().next(true);
        } else {
            this.showUploadOptions = false;
            this.showCameraDialog = true;
            this.cameraDialogOpenChange.emit(true);
        }
    }

    onCloseCameraDialog() {
        this.showCameraDialog = false;
        this.cameraDialogOpenChange.emit(false);
    }

    async onCaptureImage(imageUrl: string) {
        // close dialog
        this.showCameraDialog = false;
        this.cameraDialogOpenChange.emit(false);

        // display image and skills
        this.fileUploadType = UploadType.IMAGE;
        this.selectedFile = null; // clearing the attached file before setting the image.
        this.selectedPhoto = await this.createFileFromImageUrl(imageUrl);
        this.imageUrl = imageUrl;
        if (this.showUserInputOptions) {
            this.displayUserInputOptions(this.fileUploadType);
        }
    }

    setImageDataUrl(file: File) {
        const reader = new FileReader();
        reader.onload = (event: any) => {
            this.imageUrl = event.target.result;
        };
        reader.readAsDataURL(file);
    }

    /**
     * Prompts the user to select an action for the uploaded file.
     * If the file is an image, display the vision skills.
     * @param uploadType 
     */
    displayUserInputOptions(uploadType: UploadType) {
        this.fileUploadPlaceholder = `What would you like to do with this ${uploadType.toLowerCase()}?`;
        this.chatService.getSkills(this.config.profileId, SkillType.VISION).subscribe(options => {
            let filteredOptions = options;
            // Mobile app filtering
            if (this.isMobileApp || this.isCCU) {
                filteredOptions = filteredOptions.filter(opt =>
                    opt.presentationData?.isSupportedInMobile !== false
                );
            }
            // PDF-specific filtering (only on web)
            if ((!this.isMobileApp && !this.isCCU) && uploadType === UploadType.PDF) {
                filteredOptions = filteredOptions.filter(option =>
                    option.presentationData?.supportedFiles?.some((file: any) => file.toLowerCase() === UploadType.PDF.toLowerCase())
                );
            }
            this.inputOptions = filteredOptions;
        })
    }

    onUploadFile(from?: string) {
        if (!this.isMobileApp) {
            this.showUploadOptions = false;
        }

        const input = this.fileUpload?.nativeElement;
        if (!input) {
            return;
        }

        if (this.isCCU && from === 'camera') {
            input.accept = 'image/*';
        } else if (this.isCCU) {
            input.accept = '*/*';
        }

        input.click();
    }

    onResetFileUpload(event: any) {
        event.stopPropagation();
        this.resetFileUpload();
        this.indexedDbService.clearIndexedDbStore();
    }

    resetFileUpload() {
        if (this.fileUpload?.nativeElement) {
            this.fileUpload.nativeElement.value = '';
        }
        this.selectedFile = null;
        this.imageUrl = null;
        this.selectedPhoto = null;
        this.quickOptionPrompt = '';
        this.fileUploadPlaceholder = '';
        this.inputOptions = [];
        this.clearInput();
    }

    ngAfterViewInit() {
        if (this.fileUpload?.nativeElement) {
            this.fileUpload.nativeElement.addEventListener('cancel', (event: any) => {
                // Reset the showUploadOptions if the file upload is cancelled
                if (this.isMobileApp) {
                    this.showUploadOptions = false;
                }
            });
        }
    }

    /**
     * Once an option is selected, populate the input with the provided placeholder and hide the skills.
     * @param option Selected option
     */
    onSelectOption(option: ChatOption) {
        let placeholderText = option.userInput;
        if (placeholderText.includes(ReservedTags.ENTITY_REF)) {
            placeholderText = placeholderText.replace(ReservedTags.ENTITY_REF, "@");
            this.setInputText(placeholderText);
            this.openMentionsMenu();
        } else {
            this.setInputText(placeholderText);
            this.sendMessage(); // Auto-submit the quick options that are not mention-related.
        }
    }

    onToggleMic() {
        this.micEnabled = !this.micEnabled;
        this.toggleMic.emit(this.micEnabled);
    }

    /**
     * Invoked on text as it is typed.
     * If a placeholder is shown, hide it.
     * @param event Text event
     */
    onInputChange(event: any) {
        const text = event.target.textContent ?? '';
        this.inputText = text;
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault(); // Prevent newline if needed
            this.onEnter(event);    // Call your existing method
        }
    }

    onInputChangeTextArea(event: Event): void {
        const value = (event.target as HTMLTextAreaElement).value;
        this.inputText = value;
        const textarea = this.inputElementText.nativeElement;
        textarea.style.height = 'auto'; // Reset
        const newHeight = Math.min(textarea.scrollHeight, 120); // Limit to 120px
        textarea.style.height = `${newHeight}px`;
    }

    /**
     * The default behavior of the 'Enter' key is to move to a newline. We want to prevent this behavior.
     * @param event 
     */
    onEnter(event: any) {
        event.preventDefault();
    }

    /**
     * If a user is typing, reset the user input options.
     * @param event 
     */
    onKeyUp(_: any) {
        this.showUserInputOptions = true;
    }

    /**
     * Since we're using a contenteditable div instead of an input element, we need to intercept paste events.
     * @param event 
     */
    onPaste(event: any) {
        event.preventDefault();

        const clipboardData = event.clipboardData;
        const pastedText = clipboardData?.getData('text');

        if (pastedText) {
            // Insert the pasted text at the cursor position in the contenteditable div
            document.execCommand('insertHTML', false, pastedText);

            // Dispatch a new 'input' event
            const inputEvent = new Event('input', { bubbles: true });
            event.target?.dispatchEvent(inputEvent);
        }
    }

    /**
     * Set the focus on the input element
     */
    focus() {
        if (this.isEmbedded && this.inputElementText?.nativeElement) {
            this.inputElementText.nativeElement.focus();
        } else if (!this.isEmbedded && this.inputElement?.nativeElement) {
            this.inputElement.nativeElement.focus();
        }
    }

    // submit the msg through the send icon
    sendMessage() {
        this.sendMessgae.emit(this.inputText);
    }

    private placeCaretAtEnd(el: HTMLElement) {
        el.focus();
        if (typeof window.getSelection != 'undefined' && typeof document.createRange != 'undefined') {
            let range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            let sel: Selection | null = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
        } else if (typeof document.body.getAttribute('createTextRange') != 'undefined') {
            let textRange = (document.body as any).createTextRange();
            textRange.moveToElementText(el);
            textRange.collapse(false);
            textRange.select();
        }
    }

    private async createFileFromImageUrl(imageUrl: string): Promise<Blob> {
        const response = await fetch(imageUrl);
        const data = await response.blob();
        return new Blob([data], { type: 'image/png' });
    }
}
